import { Button, ITableColumn, Label, Loader, Table, TextAlign } from '@busybusy/webapp-react-ui';
import { CertificationEquipmentLink, Equipment } from '__generated__/graphql';
import classNames from 'classnames';
import { EmptyState, Header, PanelContent } from 'components';
import DeleteConfirmationDialog from 'containers/photos/DeleteConfirmationDialog/DeleteConfirmationDialog';
import HeaderText from 'containers/settings/components/HeaderText/HeaderText';
import { useOpenable } from 'hooks';
import { t } from 'i18next';
import { isNil } from 'lodash';
import { ReactNode, useEffect, useState } from 'react';
import { ClassName } from 'types/ClassName';
import IEquipment from 'types/Equipment';
import { isoTimeStampUtc } from 'utils/dateUtils';
import EquipmentCertificationLinkFormDialog, {
  EquipmentCertificationsDetailsData,
} from './EquipmentCertificationLinkForm/EquipmentCertificationLinkFormDialog';
import './EquipmentCertificationsDetails.scss';
import ManageEquipmentCertificationsActionBar from './ManageEquipmentCertificationsActionBar';
import { useCertificationEquipmentLinks } from './useCertificationEquipmentLinks';
import { useMutateCertificationEquipmentLinks } from './useMutateCertificationEquipmentLinks';

export interface IEquipmentCertificationsDetailsProps {
  className?: ClassName;
  equipment: IEquipment | Equipment;
}

const EquipmentCertificationsDetails = (props: IEquipmentCertificationsDetailsProps) => {
  const { className, equipment } = props;

  const deleteOpenable = useOpenable();
  const formOpenable = useOpenable();
  const [scroller, setScroller] = useState<HTMLDivElement | undefined>(undefined);
  const { data, loading, error, loadedAll, clearData } = useCertificationEquipmentLinks({
    scroller,
    equipmentId: equipment.id,
  });
  const [checkedItems, setCheckedItems] = useState<EquipmentCertificationsDetailsData[]>([]);
  const [selectedItem, setSelectedItem] = useState<EquipmentCertificationsDetailsData | undefined>();
  const { updateCertificationEquipmentLink } = useMutateCertificationEquipmentLinks();

  useEffect(() => {
    clearData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleNewRequirementClick() {
    formOpenable.open();
  }

  function renderEmptyState(): ReactNode {
    return (
      !loading && (
        <EmptyState
          title={t('No Certifications')}
          subtitle={t('Use the New Requirement button above to get started.')}
        />
      )
    );
  }

  function renderColumns(): Array<ITableColumn<EquipmentCertificationsDetailsData>> {
    return [
      {
        cell: renderNameColumn,
        title: t('Name'),
        key: 'name',
        sort: false,
        align: TextAlign.LEFT,
      },
      {
        cell: renderDescriptionColumn,
        title: t('Description'),
        key: 'description',
        sort: false,
        align: TextAlign.LEFT,
      },
    ];
  }

  function renderNameColumn(row: EquipmentCertificationsDetailsData) {
    return <Label>{row.certification.name}</Label>;
  }

  function renderDescriptionColumn(
    row: Pick<CertificationEquipmentLink, 'id' | 'certificationId' | 'equipmentId' | 'cursor' | 'certification'>
  ) {
    return <Label>{row.certification.description}</Label>;
  }

  function handleRowClick(row: EquipmentCertificationsDetailsData) {
    setSelectedItem(row);
    formOpenable.open();
  }

  function handleSelectChange(newCheckedRows: EquipmentCertificationsDetailsData[]) {
    setCheckedItems(newCheckedRows);
  }

  function renderActionBar() {
    return (
      <ManageEquipmentCertificationsActionBar
        checkedIds={checkedItems.map((item) => item.id)}
        onClear={() => setCheckedItems([])}
        onDelete={deleteOpenable.open}
      />
    );
  }

  function handleDataChange() {
    clearData();
    setSelectedItem(undefined);
    setCheckedItems([]);
  }

  async function handleDelete() {
    if (!isNil(selectedItem)) {
      await updateCertificationEquipmentLink({ id: selectedItem.id, deletedOn: isoTimeStampUtc() });
    } else if (checkedItems.length > 0) {
      const promises = checkedItems.map((item) => {
        return updateCertificationEquipmentLink({ id: item.id, deletedOn: isoTimeStampUtc() });
      });
      await Promise.all(promises);
    }
    handleDataChange();
    deleteOpenable.close();
    formOpenable.close();
  }

  function handleSubmit() {
    handleDataChange();
    formOpenable.close();
  }

  const classes = classNames('equipment-certifications-details mt-6', className);

  return (
    <div className={classes}>
      <HeaderText title={t('Required Certifications')} className="title-label mb-6">
        <div className="details-label">
          {t('Employees are required to have the following active certifications in order to operate the equipment.')}
        </div>
      </HeaderText>
      <Header actionBar={renderActionBar()}>
        <Button className="mb-6" type="secondary" rounded={true} onClick={handleNewRequirementClick}>
          {t('New Requirement')}
        </Button>
      </Header>
      <PanelContent className="panel-content" forwardRef={setScroller}>
        {scroller && (
          <Table<EquipmentCertificationsDetailsData>
            cols={renderColumns()}
            data={data}
            strokeCols={false}
            lazyLoad={false}
            minWidth={`${renderColumns().length * 200}px`}
            onRowClick={handleRowClick}
            selected={checkedItems}
            header="standard"
            onSelectChange={handleSelectChange}
            emptyTemplate={renderEmptyState()}
          />
        )}
      </PanelContent>
      <Loader isOpen={loading} overlay={true} />
      <DeleteConfirmationDialog
        isOpen={deleteOpenable.isOpen}
        onClose={deleteOpenable.close}
        deleteText={t('Delete')}
        onDelete={handleDelete}
        title={t('Delete Certification Requirement?')}
        showHeaderDivider={false}
      >
        {t(`If you delete these items they will be gone forever. Are you sure you want to proceed?`)}
      </DeleteConfirmationDialog>
      <EquipmentCertificationLinkFormDialog
        isOpen={formOpenable.isOpen}
        onClose={() => {
          setSelectedItem(undefined);
          formOpenable.close();
        }}
        equipment={equipment}
        certificationEquipmentLink={selectedItem}
        onDelete={deleteOpenable.open}
        onSubmit={handleSubmit}
      />
    </div>
  );
};

export default EquipmentCertificationsDetails;
